// src/components/dialog/DlgPanelSummary.tsx

import { DeleteTwoTone, EditTwoTone, SaveTwoTone } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { Chart } from 'react-google-charts'

import { PanelId } from '@/types/domain'
import { FolderId, FolderName, FolderPool } from '@/types/domain/folder'
import { SensorId, SensorPool } from '@/types/domain/sensor'
import { KARMA_ConfirmDialog } from '@/types/karma/dialog'

import { useFolderPool, useSensorPool } from '../../hooks'
import { theme } from '../../pages/karma.theme'

import { DlgCloseButton } from './DlgCloseButton'

type DlgPanelSummaryProps = KARMA_ConfirmDialog & {
  data: {
    folders: FolderId[]
    sensors: SensorId[]
    panelId: PanelId
  }
  onDeletePanel?: () => void
  onEditPanel?: () => void
  onGotoPrev: () => void
}

export const DlgPanelSummary = ({
  data: iData,
  onCancel,
  onConfirm,
  onDeletePanel,
  onEditPanel,
  onGotoPrev,
  open,
}: DlgPanelSummaryProps) => {
  const { folders, sensors, panelId } = iData

  const fPool: FolderPool = useFolderPool()
  const sPool: SensorPool = useSensorPool()

  const data = [
    ['Ausgewählte Gebäude', 'Ausgewählte Sensoren'],
    ...fPool
      .filter(f => folders.includes(Number(f.id)))
      .map(f => {
        const fName: FolderName = f.attributes.name
        const sNames: string[] = sPool
          .filter(
            s =>
              sensors.includes(Number(s.id)) &&
              s.attributes.folder_id === Number(f.id)
          )
          .map(s => s.attributes.name)
        return [fName, sNames.join('<br/>')]
      }),
  ]

  return (
    <Dialog fullWidth onClose={onCancel} open={open}>
      <DialogTitle>
        <Typography>
          {panelId === null && 'Neue Kachel - '}Zusammenfassung
        </Typography>

        <DlgCloseButton onClick={onCancel} />
      </DialogTitle>

      <DialogContent>
        <style>
          {`
                            .google-visualization-table-table {
                              border: 0 !important;
                            }
                            .google-visualization-table-type-number.google-visualization-table-td {
                              font-weight: bold;
                              text-align: center;
                            }
                            .google-visualization-table-tr,
                            .google-visualization-table-td,
                            .google-visualization-table-th {
                              border-left: none !important;
                              border-right: none !important;
                              border-bottom: 2px dotted #ccc !important;
                            }
                            .google-visualization-table-sortind {
                              color: ${theme.palette.primary.main};
                              display: inline !important;
                            }
                            .unsorted .google-visualization-table-sortind::after {
                              content: "▼";
                              display: inline !important;
                              color: #ccc;
                            }
                            .google-visualization-table-table tbody tr:last-child td,
                            .google-visualization-table-table tbody tr:last-child th {
                              border-bottom: none !important;
                            }
                            .google-visualization-table-th {
                              background-color: #fff;
                              border-bottom: 1px solid #ccc !important;
                              border-top: 1px solid #ccc !important;
                              font-size: 18px !important;
                            }
                            .google-visualization-table-th:first-child {
                              text-align: left !important;
                            }
                            .google-visualization-table-tr:last-child {
                              border-bottom: none !important;
                            }
              `}
        </style>
        <Stack sx={{ my: 2 }}>
          <Chart
            chartLanguage={'de_DE'}
            chartType={'Table'}
            data={data}
            options={{
              allowHtml: true,
              chartArea: {
                height: '500px',
                width: '600px',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
              },
              height: 500,
              backgroundColor: 'white',
              tooltip: { isHtml: true },
              legend: 'none',
              sortColumn: 1,
              sortAscending: false,
              sort: 'enable',
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        {panelId !== null && (
          <Button onClick={onDeletePanel}>
            <DeleteTwoTone />
            Löschen
          </Button>
        )}
        {panelId !== null && (
          <Button onClick={onEditPanel}>
            <EditTwoTone />
            Bearbeiten
          </Button>
        )}

        <Button onClick={onGotoPrev}>Zurück</Button>
        <Button onClick={onConfirm}>
          <SaveTwoTone />
          {panelId === null ? 'Speichern' : 'Aktualisieren'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
